import { MODEL_INPUTS, MODEL_OUTPUTS } from '@crowdex/provider-contracts';
import { z } from 'zod';

export const pluginSettingValueSchema = z.union([
  z.string().max(16000),
  z.number().finite(),
  z.boolean(),
]);
export type PluginSettingValue = z.infer<typeof pluginSettingValueSchema>;
const keySchema = z
  .string()
  .regex(/^[a-zA-Z][a-zA-Z0-9._-]{0,95}$/)
  .refine((key) => !['__proto__', 'constructor', 'prototype'].includes(key));
/**
 * A string setting the core fills from the user's providers: a model (`<provider>/<model>`) that
 * makes `output` (and takes `input`), or a voice of the model another setting (`model`) names.
 * Empty is the user's default (Settings › Models), or the provider's voice.
 */
export const pluginSettingPickerSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('model'),
      output: z.enum(MODEL_OUTPUTS),
      input: z.array(z.enum(MODEL_INPUTS)).min(1).max(MODEL_INPUTS.length).optional(),
    })
    .strict(),
  z.object({ kind: z.literal('voice'), model: keySchema }).strict(),
]);
export type PluginSettingPicker = z.infer<typeof pluginSettingPickerSchema>;
export const pluginSettingSchema = z
  .object({
    title: z.string().min(1).max(160),
    description: z.string().max(2000).optional(),
    type: z.enum(['string', 'number', 'boolean']),
    default: pluginSettingValueSchema,
    scope: z.enum(['user', 'project']).default('project'),
    choices: z.array(pluginSettingValueSchema).min(1).max(100).optional(),
    minimum: z.number().finite().optional(),
    maximum: z.number().finite().optional(),
    picker: pluginSettingPickerSchema.optional(),
  })
  .strict()
  .superRefine((field, ctx) => {
    if (field.picker !== undefined && (field.type !== 'string' || field.choices !== undefined))
      ctx.addIssue({ code: 'custom', message: 'A picker is a string setting without choices' });
    if (!validSetting(field, field.default))
      ctx.addIssue({ code: 'custom', message: 'Invalid setting default' });
    if (field.choices?.some((value) => typeof value !== field.type))
      ctx.addIssue({ code: 'custom', message: 'Choices must match the setting type' });
    if ((field.minimum !== undefined || field.maximum !== undefined) && field.type !== 'number')
      ctx.addIssue({ code: 'custom', message: 'Bounds require a number' });
    if (field.minimum !== undefined && field.maximum !== undefined && field.minimum > field.maximum)
      ctx.addIssue({ code: 'custom', message: 'Invalid bounds' });
  });
export const pluginSettingsSchema = z
  .unknown()
  .superRefine((value, ctx) => {
    if (value && typeof value === 'object' && Object.hasOwn(value, '__proto__'))
      ctx.addIssue({ code: 'custom', message: 'Invalid setting key' });
  })
  .pipe(z.record(keySchema, pluginSettingSchema))
  .refine((value) => Object.keys(value).length <= 100)
  .refine(
    (value) =>
      Object.values(value).every(
        (field) =>
          field.picker?.kind !== 'voice' || value[field.picker.model]?.picker?.kind === 'model',
      ),
    'A voice setting names a model setting',
  );
export type PluginSettings = z.infer<typeof pluginSettingsSchema>;
export type PluginSettingsValues = Record<string, PluginSettingValue>;
export type PluginSettingsPatch = Record<string, PluginSettingValue | null>;
export type PluginSettingsSnapshot = {
  values: PluginSettingsValues;
  overrides: PluginSettingsValues;
  sources: Record<string, 'default' | 'user' | 'project'>;
};
export function validSetting(
  field: {
    type: string;
    choices?: PluginSettingValue[] | undefined;
    minimum?: number | undefined;
    maximum?: number | undefined;
  },
  value: unknown,
): value is PluginSettingValue {
  return (
    typeof value === field.type &&
    (typeof value !== 'number' ||
      (Number.isFinite(value) &&
        (field.minimum === undefined || value >= field.minimum) &&
        (field.maximum === undefined || value <= field.maximum))) &&
    (typeof value !== 'string' || value.length <= 16000) &&
    (!field.choices || field.choices.includes(value as PluginSettingValue))
  );
}
export function validateSettingsPatch(
  schema: PluginSettings,
  patch: PluginSettingsPatch,
  project: boolean,
) {
  for (const [key, value] of Object.entries(patch)) {
    const field = Object.hasOwn(schema, key) ? schema[key] : undefined;
    if (
      !field ||
      (project && field.scope === 'user') ||
      (value !== null && !validSetting(field, value))
    )
      throw new Error(`Invalid plugin setting: ${key}`);
  }
}
export function resolvePluginSettings(
  schema: PluginSettings,
  user: PluginSettingsPatch = {},
  project?: PluginSettingsPatch,
): PluginSettingsSnapshot {
  const values: PluginSettingsValues = {};
  const overrides: PluginSettingsValues = {};
  const sources: PluginSettingsSnapshot['sources'] = {};
  for (const [key, field] of Object.entries(schema)) {
    values[key] = field.default;
    sources[key] = 'default';
    if (Object.hasOwn(user, key) && validSetting(field, user[key])) {
      values[key] = user[key];
      sources[key] = 'user';
    }
    if (
      project &&
      field.scope === 'project' &&
      Object.hasOwn(project, key) &&
      validSetting(field, project[key])
    ) {
      values[key] = project[key];
      sources[key] = 'project';
    }
    const stored = (project ?? user)[key];
    if ((!project || field.scope === 'project') && validSetting(field, stored))
      overrides[key] = stored;
  }
  return { values, overrides, sources };
}

/** Read-only API injected into isolated plugin activation. Scope and ownership are host-selected. */
export type PluginHostSettings = {
  get(): Promise<PluginSettingsSnapshot>;
  subscribe(
    listener: (snapshot: PluginSettingsSnapshot) => void,
    onError?: (error: unknown) => void,
  ): () => void;
};
